import axios from "axios";
import { config } from "src/utils/config";

let publicCatalogCache = null;
let publicCatalogRequest = null;

export function peekPublicCatalogCache() {
  return publicCatalogCache;
}

export function loadPublicCatalogs({ force = false } = {}) {
  if (!force && publicCatalogCache) {
    return Promise.resolve(publicCatalogCache);
  }

  if (!force && publicCatalogRequest) {
    return publicCatalogRequest;
  }

  publicCatalogRequest = fetchPublicCatalogs()
    .then((catalogs) => {
      publicCatalogCache = catalogs;
      return catalogs;
    })
    .finally(() => {
      publicCatalogRequest = null;
    });

  return publicCatalogRequest;
}

export async function fetchPublicCatalogs() {
  const { data } = await axios.get(`${config.apiUrl}/list/catalogs`, {
    timeout: 60000,
  });

  if (!data?.status) {
    throw new Error(data?.message || "Failed to load catalogs");
  }

  return data.data || [];
}

/**
 * Backend sometimes returns a stringified string-array where inner quotes
 * (e.g. inches: 4.2") were not escaped, so JSON.parse fails. Recover by
 * splitting on the `", "` item delimiter after stripping outer brackets.
 */
function parseLooseStringArray(raw) {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) return null;

  const inner = trimmed.slice(1, -1).trim();
  if (!inner) return [];

  const parts = inner.split('", "');
  return parts
    .map((part, index) => {
      let value = part;
      if (index === 0 && value.startsWith('"')) value = value.slice(1);
      if (index === parts.length - 1 && value.endsWith('"')) {
        value = value.slice(0, -1);
      }
      return value.trim();
    })
    .filter(Boolean);
}

function parseDescriptionString(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    try {
      return unwrapDescriptionValue(JSON.parse(trimmed));
    } catch {
      const loose = parseLooseStringArray(trimmed);
      if (loose) return loose;
      return [trimmed];
    }
  }

  return [trimmed];
}

function unwrapDescriptionValue(value) {
  if (typeof value === "string") {
    return parseDescriptionString(value);
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (typeof item === "string") {
        const trimmed = item.trim();
        if (!trimmed) return [];

        // Some catalogs store one stringified array as a single list item.
        if (trimmed.startsWith("[")) {
          return parseDescriptionString(trimmed);
        }

        return [trimmed];
      }

      if (Array.isArray(item)) {
        return unwrapDescriptionValue(item);
      }

      if (item == null || item === "") return [];
      return [String(item)];
    });
  }

  return [];
}

export function parseCatalogDescription(description) {
  if (description == null || description === "") return [];
  return unwrapDescriptionValue(description);
}

export function mapCatalogToProduct(catalog) {
  const description = parseCatalogDescription(catalog?.description);

  return {
    cover: catalog?.images?.[0],
    name: catalog?.title,
    desc: description,
    breadCrumb: "Catalog",
    ...catalog,
    images: catalog?.images || [],
    description,
  };
}

export function catalogsById(catalogs = []) {
  return catalogs.reduce((acc, catalog) => {
    if (catalog?.id != null) {
      acc[catalog.id] = catalog;
    }
    return acc;
  }, {});
}

export function enrichLandingProducts(products, catalogs = []) {
  const byId = catalogsById(catalogs);

  return products
    .map((product) => {
      if (product.catalogId == null) return null;

      const catalog = byId[product.catalogId];
      if (!catalog) return null;

      const title = catalog.title || catalog.name;
      const image = catalog.images?.[0] || catalog.cover;

      if (!title || !image) return null;

      return {
        ...product,
        title,
        image,
      };
    })
    .filter(Boolean);
}
